import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import GameCard from '@/components/base/GameCard';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, games, articles, articleCategories } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'games' | 'articles'>('games');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredGames = games.filter((g) => {
    if (!slug) return true;
    return g.category === slug;
  });

  const filteredArticles = articles.filter((a) => {
    if (!slug) return true;
    if (slug === 'xin-pin-tuijian') return a.category === 'tuijian';
    if (slug === 'dianzi-youxi') return true;
    return a.category === slug || true;
  });

  useEffect(() => {
    setActiveTab('games');
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <p className="text-foreground-600">未找到该分类</p>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const displayGames = activeTab === 'games' ? filteredGames : [];
  const hasArticlesTab = filteredArticles.length > 0;

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: currentCategory.name },
          ]}
        />

        {/* Category Header */}
        <section className="bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg bg-primary-100">
                <i className={`${currentCategory.icon} text-2xl md:text-3xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1 max-w-lg">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 md:gap-2 flex-wrap py-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content Tabs: Games / Articles */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3">
              <button
                onClick={() => setActiveTab('games')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  activeTab === 'games'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className="ri-gamepad-line mr-1"></i>游戏
              </button>
              {hasArticlesTab && (
                <button
                  onClick={() => setActiveTab('articles')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    activeTab === 'articles'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-article-line mr-1"></i>攻略
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Games Grid */}
        {activeTab === 'games' && (
          <section className="py-8 md:py-10 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              {displayGames.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {displayGames.map((game) => (
                    <GameCard
                      key={game.id}
                      slug={game.slug}
                      name={game.name}
                      categoryName={game.categoryName}
                      rating={game.rating}
                      players={game.players}
                      tags={game.tags}
                      image={game.image}
                      isNew={game.isNew}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-gamepad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无游戏</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Articles List */}
        {activeTab === 'articles' && (
          <section className="py-8 md:py-10 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col gap-4">
                {filteredArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    categoryName={article.categoryName}
                    author={article.author}
                    date={article.date}
                    readTime={article.readTime}
                    image={article.image}
                    tags={article.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - PG游戏官方平台`,
            description: currentCategory.description,
            url: `https://www.pggame-official.com/category/${currentCategory.slug}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.pggame-official.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory.name,
                item: `https://www.pggame-official.com/category/${currentCategory.slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}